import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Bell, Play, Clock, Mail, MessageSquare, CheckCircle, AlertCircle, RefreshCw, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { EmailTemplatesPanel } from '../../components/EmailTemplatesPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REMINDER_INFO = [
  { type: '7d_balance', label: '7-Day Balance Reminder', channel: 'Email', timing: '7 days before party', description: 'Sent to customers with unpaid balance', color: '#FFD166' },
  { type: '2d_details', label: 'Party Details', channel: 'Email', timing: '2 days before party', description: 'Party location, what to bring, arrival instructions', color: '#06D6A0' },
  { type: '1d_sms', label: 'SMS Confirmation', channel: 'SMS', timing: '24 hours before party', description: 'Quick text reminder with party time and address', color: '#6366F1' },
  { type: 'post_thankyou', label: 'Thank You + Review', channel: 'Email', timing: 'Day after party', description: 'Thank you, review request, and re-booking discount', color: '#FF3B6B' },
];

export const CrmReminders = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => { fetchLogs(); }, []);

  const fetchLogs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/reminder-log`, { withCredentials: true });
      setLogs(res.data);
    } catch (e) { toast.error('Failed to load logs'); }
    finally { setLoading(false); }
  };

  const runReminders = async () => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API_URL}/api/cron/process-reminders`, {}, { withCredentials: true });
      setLastResult(res.data);
      toast.success(`Reminders processed: ${res.data.sent} sent, ${res.data.skipped} skipped`);
      fetchLogs();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to process reminders');
    } finally {
      setProcessing(false);
    }
  };

  const getTypeLabel = (type) => {
    const info = REMINDER_INFO.find(r => r.type === type);
    return info?.label || type;
  };

  const getTypeColor = (type) => {
    const info = REMINDER_INFO.find(r => r.type === type);
    return info?.color || '#6B6B7A';
  };

  if (loading) return <div className="flex items-center justify-center h-[60vh]"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF3B6B]" /></div>;

  return (
    <div>
      <div className="flex justify-between items-start mb-8">
        <div>
          <h1 className="text-3xl font-semibold text-[#1E1E24]" style={{ fontFamily: 'Fredoka, sans-serif' }}>Automated Reminders</h1>
          <p className="text-[#6B6B7A]">Email & SMS sequences with no-duplicate tracking</p>
        </div>
        <Button onClick={runReminders} disabled={processing}
          className="bg-[#FF3B6B] hover:bg-[#E62E58] text-white rounded-full" data-testid="run-reminders-btn">
          {processing ? (
            <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
          ) : (
            <><Play className="w-4 h-4 mr-2" /> Run Reminders Now</>
          )}
        </Button>
      </div>

      {/* Last Run Result */}
      {lastResult && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl border border-[#E8E5DF] p-6 mb-6">
          <h3 className="font-semibold text-[#1E1E24] mb-3" style={{ fontFamily: 'Fredoka' }}>Last Run Result</h3>
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-3 bg-[#06D6A0]/10 rounded-xl">
              <p className="text-2xl font-bold text-[#06D6A0]" style={{ fontFamily: 'Fredoka' }}>{lastResult.sent}</p>
              <p className="text-xs text-[#6B6B7A]">Sent</p>
            </div>
            <div className="text-center p-3 bg-[#FFD166]/10 rounded-xl">
              <p className="text-2xl font-bold text-[#B8860B]" style={{ fontFamily: 'Fredoka' }}>{lastResult.skipped}</p>
              <p className="text-xs text-[#6B6B7A]">Skipped (already sent)</p>
            </div>
            <div className="text-center p-3 bg-[#FF3B6B]/10 rounded-xl">
              <p className="text-2xl font-bold text-[#FF3B6B]" style={{ fontFamily: 'Fredoka' }}>{lastResult.failed}</p>
              <p className="text-xs text-[#6B6B7A]">Failed</p>
            </div>
          </div>
          {lastResult.details?.length > 0 && (
            <div className="space-y-2">
              {lastResult.details.map((d, i) => (
                <div key={i} className="flex items-center gap-3 text-sm p-2 bg-[#FFFDF8] rounded-lg">
                  <CheckCircle className="w-4 h-4 text-[#06D6A0]" />
                  <span className="font-medium" style={{ color: getTypeColor(d.type) }}>{getTypeLabel(d.type)}</span>
                  <span className="text-[#6B6B7A]">&rarr; {d.to}</span>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6 grid w-full max-w-xl grid-cols-3">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white">
            <Bell className="w-4 h-4 mr-2" /> Sequences
          </TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white" data-testid="reminders-templates-tab">
            <FileText className="w-4 h-4 mr-2" /> Email Templates
          </TabsTrigger>
          <TabsTrigger value="log" className="data-[state=active]:bg-[#FF3B6B] data-[state=active]:text-white">
            <Clock className="w-4 h-4 mr-2" /> Send Log
          </TabsTrigger>
        </TabsList>

        {/* Sequences Overview */}
        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {REMINDER_INFO.map((r, i) => (
              <motion.div key={r.type} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                className="bg-white rounded-2xl border border-[#E8E5DF] p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${r.color}15` }}>
                    {r.channel === 'SMS' ? (
                      <MessageSquare className="w-5 h-5" style={{ color: r.color }} />
                    ) : (
                      <Mail className="w-5 h-5" style={{ color: r.color }} />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-[#1E1E24]">{r.label}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${r.channel === 'SMS' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {r.channel}
                      </span>
                    </div>
                    <p className="text-sm text-[#6B6B7A] mb-1">{r.description}</p>
                    <p className="text-xs font-medium" style={{ color: r.color }}>Triggers: {r.timing}</p>
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs text-[#6B6B7A]">
                        Sent: {logs.filter(l => l.reminder_type === r.type).length}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-6 bg-[#FFFDF8] rounded-2xl border border-[#E8E5DF] p-6">
            <h3 className="font-semibold text-[#1E1E24] mb-3" style={{ fontFamily: 'Fredoka' }}>How It Works</h3>
            <div className="space-y-3 text-sm text-[#6B6B7A]">
              <p>1. Click <strong>"Run Reminders Now"</strong> to process all due reminders for upcoming/past parties.</p>
              <p>2. The system checks each booking against the 4 trigger rules (7d, 2d, 1d, post).</p>
              <p>3. Reminders that were already sent are <strong>automatically skipped</strong> (no duplicates).</p>
              <p>4. Each send is logged with timestamp, provider, and status.</p>
              <p>5. For production, set up an external cron to call <code>POST /api/cron/process-reminders</code> daily.</p>
            </div>
          </div>
        </TabsContent>

        {/* Email Templates */}
        <TabsContent value="templates">
          <EmailTemplatesPanel />
        </TabsContent>

        {/* Send Log */}
        <TabsContent value="log">
          <div className="bg-white rounded-2xl border border-[#E8E5DF] overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Booking</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sent At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#6B6B7A]">No reminders sent yet. Click "Run Reminders Now" to process.</TableCell></TableRow>
                ) : logs.map((log, i) => (
                  <TableRow key={log.log_id || i}>
                    <TableCell>
                      <span className="px-2 py-1 rounded-full text-xs font-medium"
                        style={{ backgroundColor: `${getTypeColor(log.reminder_type)}15`, color: getTypeColor(log.reminder_type) }}>
                        {getTypeLabel(log.reminder_type)}
                      </span>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{log.booking_id}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${log.provider === 'mock' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'}`}>
                        {log.provider}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`flex items-center gap-1 text-xs ${log.status?.includes('sent') ? 'text-[#06D6A0]' : 'text-[#FF3B6B]'}`}>
                        {log.status?.includes('sent') ? <CheckCircle className="w-3 h-3" /> : <AlertCircle className="w-3 h-3" />}
                        {log.status}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-[#6B6B7A]">{new Date(log.sent_at).toLocaleString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CrmReminders;
